import type { FC, ReactNode } from 'react'

import { Box, Disc3, Youtube } from 'lucide-react'
import { cn } from '@/lib/utils'
import { siteConfig } from '@/constants/site'

interface SocialLinksProps {
    dark?: boolean
    compact?: boolean
    className?: string
}

const XLogo: FC<{ className?: string }> = ({ className }): ReactNode => (
    <svg viewBox='0 0 24 24' aria-hidden='true' focusable='false' className={className}>
        <path fill='currentColor' d='M17.75 3h3.07l-6.71 7.67L22 21h-6.18l-4.84-6.33L5.44 21H2.37l7.18-8.2L2 3h6.34l4.37 5.78L17.75 3Zm-1.08 16.18h1.7L7.4 4.73H5.58l11.09 14.45Z' />
    </svg>
)

const LINKS = [
    { key: 'youtube', label: "YouTube", href: siteConfig.links.youtube, icon: <Youtube className='size-[15px]' strokeWidth={1.6} /> },
    { key: 'x', label: "X", href: siteConfig.links.x, icon: <XLogo className='size-[13px]' /> },
    { key: 'fanbox', label: "FANBOX", href: siteConfig.links.fanbox, icon: <Box className='size-[15px]' strokeWidth={1.6} /> },
    { key: 'tunecore', label: "TuneCore", href: siteConfig.links.tunecore, icon: <Disc3 className='size-[15px]' strokeWidth={1.6} /> }
].filter(link => Boolean(link.href))

// Activity links as small icons in a thin pill. Hover turns an icon gold with a soft glow.
const SocialLinks: FC<SocialLinksProps> = ({ dark = false, compact = false, className }): ReactNode => {
    if (LINKS.length === 0) return null

    return (
        <ul
            aria-label='活動リンク'
            className={cn(
                'flex items-center rounded-full ring-1 backdrop-blur-md transition-colors duration-700',
                compact ? 'gap-0 p-0.5 [&_a]:size-7 [&_svg]:scale-90' : 'gap-0.5 p-1',
                dark ? 'bg-night-soft/50 ring-mist/15' : 'bg-mist/55 ring-ink/10',
                className
            )}
        >
            {LINKS.map(link => (
                <li key={link.key}>
                    <a
                        href={link.href}
                        target='_blank'
                        rel='noopener noreferrer'
                        title={link.label}
                        aria-label={`${link.label}（新しいタブで開きます）`}
                        className={cn(
                            'group/social flex size-8 items-center justify-center rounded-full transition-[color,background-color,filter] duration-500 hover:text-gold hover:drop-shadow-[0_0_6px_rgba(226,179,60,0.75)] focus-visible:outline-2 focus-visible:outline-gold',
                            dark ? 'text-mist/80 hover:bg-mist/5' : 'text-ink/75 hover:bg-gold/10'
                        )}
                    >
                        {link.icon}
                    </a>
                </li>
            ))}
        </ul>
    )
}

export default SocialLinks
